import { useEffect, useRef, type ReactElement, type ReactNode } from 'react';
import { Link, NavLink, useLocation } from 'react-router';
import {
  BITRIX_PATH,
  INBOX_PATH,
  QUICK_REPLIES_PATH,
  USERS_PATH,
  WHATSAPP_PATH,
  isAdminRoot,
} from './paths';
import styles from './AdminShell.module.css';

/**
 * Casca da administração: voltar à caixa de entrada, título, menu por links e o conteúdo da seção.
 * É uma página que rola normalmente (não é a casca de altura fixa da caixa de entrada).
 *
 * Foco: ao chegar à área ele vai para o título da página; ao trocar de seção, para o título da nova
 * seção (o link clicado continua lá, mas o conteúdo mudou e quem usa teclado ou leitor de tela
 * precisa ser levado até ele). Nunca fica no `<body>`.
 */
export function AdminShell({ children }: { children: ReactNode }): ReactElement {
  const { pathname } = useLocation();
  const titleRef = useRef<HTMLHeadingElement>(null);
  const mainRef = useRef<HTMLElement>(null);
  // null = a pessoa ainda não "chegou" a uma seção. A guarda por igualdade também cobre o modo
  // estrito do React, que roda o efeito duas vezes seguidas na montagem.
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;
    // `/admin` puro só redireciona para a primeira seção: ainda não chegou a lugar nenhum.
    if (isAdminRoot(pathname)) {
      lastPath.current = null;
      return;
    }
    const arriving = lastPath.current === null;
    lastPath.current = pathname;
    if (arriving) titleRef.current?.focus();
    else mainRef.current?.querySelector<HTMLElement>('[data-admin-heading]')?.focus();
  }, [pathname]);

  return (
    // `position: relative` contém os `.sr-only` e as regiões de aviso vazias (absolutos): sem um
    // ancestral posicionado eles esticariam o documento.
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <Link to={INBOX_PATH} className={styles.back}>
            <span aria-hidden="true">←</span> Voltar para a caixa de entrada
          </Link>
          <h1 ref={titleRef} tabIndex={-1} className={styles.title}>
            Administração
          </h1>
          <nav aria-label="Seções da administração">
            <ul className={styles.nav}>
              <li>
                <NavLink to={USERS_PATH} end className={styles.link}>
                  Usuários
                </NavLink>
              </li>
              <li>
                <NavLink to={WHATSAPP_PATH} end className={styles.link}>
                  WhatsApp
                </NavLink>
              </li>
              <li>
                <NavLink to={QUICK_REPLIES_PATH} end className={styles.link}>
                  Respostas rápidas
                </NavLink>
              </li>
              <li>
                <NavLink to={BITRIX_PATH} end className={styles.link}>
                  Bitrix
                </NavLink>
              </li>
            </ul>
          </nav>
        </div>
      </header>
      <main ref={mainRef} className={styles.main}>
        {children}
      </main>
    </div>
  );
}
